<script setup lang="ts">
import { computed } from 'vue'

import UiSpinner from './UiSpinner.vue'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    loading?: boolean
    block?: boolean
    /**
     * Why the button is disabled. Rendered as a native tooltip AND announced, so a
     * disabled control is never a dead end the operator has to guess about.
     */
    disabledReason?: string
    ariaLabel?: string
  }>(),
  {
    variant: 'secondary',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    block: false,
    disabledReason: '',
    ariaLabel: '',
  },
)

defineEmits<{ click: [MouseEvent] }>()

const isDisabled = computed(() => props.disabled || props.loading)
const title = computed(() => (props.disabled && props.disabledReason ? props.disabledReason : undefined))
</script>

<template>
  <button
    :type="type"
    :class="['ui-button', `ui-button--${variant}`, `ui-button--${size}`, { 'ui-button--block': block }]"
    :disabled="isDisabled"
    :title="title"
    :aria-label="ariaLabel || undefined"
    :aria-disabled="isDisabled ? 'true' : undefined"
    @click="$emit('click', $event)"
  >
    <UiSpinner v-if="loading" size="xs" :inline="true" />
    <slot />
    <span v-if="disabled && disabledReason" class="sr-only">. {{ disabledReason }}</span>
  </button>
</template>

<style scoped>
.ui-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--surface-3);
  color: var(--text-primary);
  font-size: var(--text-base);
  font-weight: 500;
  line-height: 1;
  padding: 0 var(--space-4);
  height: 34px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background var(--transition-fast),
    border-color var(--transition-fast),
    color var(--transition-fast);
}

.ui-button--sm {
  height: 28px;
  padding: 0 var(--space-3);
  font-size: var(--text-sm);
}

.ui-button--block {
  width: 100%;
}

.ui-button:hover:not(:disabled) {
  background: var(--surface-hover);
  border-color: var(--border-strong);
}

.ui-button--primary {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
  color: var(--accent-contrast);
  font-weight: 600;
}

.ui-button--primary:hover:not(:disabled) {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.ui-button--ghost {
  background: transparent;
  border-color: transparent;
  color: var(--text-secondary);
}

.ui-button--ghost:hover:not(:disabled) {
  background: var(--surface-hover);
  border-color: var(--border-subtle);
  color: var(--text-primary);
}

.ui-button--danger {
  background: transparent;
  border-color: var(--danger);
  color: var(--danger);
}

.ui-button--danger:hover:not(:disabled) {
  background: var(--danger-soft);
  border-color: var(--danger);
}

.ui-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
